/**
 * MobX store for the admin "Download Open RMAs" card: portal selection,
 * POST /rms-api/admin-download-open-rmas, browser download of streamed CSV, and message or error UI.
 */
import { makeAutoObservable, runInAction } from "mobx";
import AdminMigrateGlobalEMerchantsService, {
    DownloadOpenRmasResult,
} from "../../../../services/api/admin-migrate-global-e-merchants-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import { IShop } from "../../../../models/User";
import { isSet } from "../../../../models/model-types-methods";

class DownloadOpenRmasStore {
    private _selectedShop: IShop | null | undefined = null;
    private _isProcessing = false;
    private _infoMessage: string | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get portal(): IShop | null | undefined {
        return this._selectedShop;
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get infoMessage(): string | null {
        return this._infoMessage;
    }

    public setPortal(shop: IShop | null | undefined): void {
        this._selectedShop = shop;
        this._infoMessage = null;
    }

    public async download(): Promise<void> {
        if (!isSet(this._selectedShop) || this._isProcessing) {
            return;
        }

        this._isProcessing = true;
        this._infoMessage = null;

        try {
            const result = await this.service.downloadOpenRmas(this._selectedShop.id);
            if (result.kind === "message") {
                runInAction(() => {
                    this._infoMessage = result.message;
                });
                return;
            }
            this.triggerBlobDownload(result);
        }
        finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }

    private triggerBlobDownload(result: Extract<DownloadOpenRmasResult, { kind: "file" }>): void {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }
}

export default new DownloadOpenRmasStore();
